import type { ZoneDef } from '@gra/content';
import { hurtPlayer } from './enemy-ai';
import { addDrop } from './loot';
import { chunkObstacles, hash, OBSTACLE_CHUNK } from './obstacles';
import { nextRandom } from './rng';
import type { GameState } from './types';

/**
 * Elementy mapy poza przeszkodami: kałuże (trucizna, lód, żar) i obiekty do rozbicia.
 * Położenie wynika z seeda wyprawy i kawałka mapy, tak jak przy przeszkodach. W stanie gry
 * trzymamy tylko klucze rozbitych obiektów, więc stan zostaje mały i da się go serializować.
 */

export type HazardDef = NonNullable<ZoneDef['hazards']>;

export interface Hazard {
  x: number;
  y: number;
  radius: number;
  /** 0..1 — render wybiera nim obrót i kształt plamy. */
  variant: number;
}

export interface Breakable {
  /** Klucz w `GameState.broken` po rozbiciu. */
  key: string;
  x: number;
  y: number;
  radius: number;
  variant: number;
}

/** Promień kolizji obiektu do rozbicia. */
export const BREAKABLE_RADIUS = 13;
/** Co ile sekund kałuża zadaje obrażenia (pojedyncze „ukłucia” zamiast ciągłego mrugania). */
const HAZARD_TICK = 0.5;
const CLEAR_START = 200;
/** Inne „sole” niż przeszkody — elementy nie układają się w te same miejsca. */
const HAZARD_SALT = 0x2f6b1d;
const BREAKABLE_SALT = 0x51c3a7;

const hazardCache = new Map<string, Hazard[]>();
const breakableCache = new Map<string, Breakable[]>();

function clearOfObstacles(seed: number, cx: number, cy: number, obstacles: number, x: number, y: number, r: number): boolean {
  return !chunkObstacles(seed, cx, cy, obstacles).some((o) => Math.hypot(o.x - x, o.y - y) < o.radius + r + 10);
}

/** Kałuże w jednym kawałku mapy. */
export function chunkHazards(seed: number, cx: number, cy: number, def: HazardDef, obstacles: number): Hazard[] {
  const key = `${seed}:${def.perChunk}:${def.radius[0]}:${def.radius[1]}:${obstacles}:${cx},${cy}`;
  const hit = hazardCache.get(key);
  if (hit) return hit;
  const out: Hazard[] = [];
  const s = seed ^ HAZARD_SALT;
  const [rMin, rMax] = def.radius;
  for (let i = 0; i < def.perChunk; i++) {
    const radius = rMin + hash(s, cx, cy, i * 4 + 2) * (rMax - rMin);
    const x = cx * OBSTACLE_CHUNK + rMax + hash(s, cx, cy, i * 4) * (OBSTACLE_CHUNK - 2 * rMax);
    const y = cy * OBSTACLE_CHUNK + rMax + hash(s, cx, cy, i * 4 + 1) * (OBSTACLE_CHUNK - 2 * rMax);
    if (Math.hypot(x, y) < CLEAR_START + radius) continue;
    if (!clearOfObstacles(seed, cx, cy, obstacles, x, y, radius)) continue;
    if (out.some((h) => Math.hypot(h.x - x, h.y - y) < h.radius + radius + 40)) continue;
    out.push({ x, y, radius, variant: hash(s, cx, cy, i * 4 + 3) });
  }
  if (hazardCache.size > 4000) hazardCache.clear();
  hazardCache.set(key, out);
  return out;
}

/** Obiekty do rozbicia w jednym kawałku mapy (rozbite też — filtruj przez `GameState.broken`). */
export function chunkBreakables(seed: number, cx: number, cy: number, count: number, obstacles: number): Breakable[] {
  const key = `${seed}:${count}:${obstacles}:${cx},${cy}`;
  const hit = breakableCache.get(key);
  if (hit) return hit;
  const out: Breakable[] = [];
  const s = seed ^ BREAKABLE_SALT;
  const m = 30;
  for (let i = 0; i < count; i++) {
    const x = cx * OBSTACLE_CHUNK + m + hash(s, cx, cy, i * 3) * (OBSTACLE_CHUNK - 2 * m);
    const y = cy * OBSTACLE_CHUNK + m + hash(s, cx, cy, i * 3 + 1) * (OBSTACLE_CHUNK - 2 * m);
    if (Math.hypot(x, y) < 120) continue;
    if (!clearOfObstacles(seed, cx, cy, obstacles, x, y, BREAKABLE_RADIUS)) continue;
    if (out.some((b) => Math.hypot(b.x - x, b.y - y) < 60)) continue;
    out.push({ key: `${cx},${cy},${i}`, x, y, radius: BREAKABLE_RADIUS, variant: hash(s, cx, cy, i * 3 + 2) });
  }
  if (breakableCache.size > 4000) breakableCache.clear();
  breakableCache.set(key, out);
  return out;
}

const zoneOf = (s: GameState) => s.content.zones[s.zoneId];

/** Kawałki mapy w promieniu `r` od punktu. */
function chunksAround(x: number, y: number, r: number, fn: (cx: number, cy: number) => void): void {
  const x0 = Math.floor((x - r) / OBSTACLE_CHUNK);
  const x1 = Math.floor((x + r) / OBSTACLE_CHUNK);
  const y0 = Math.floor((y - r) / OBSTACLE_CHUNK);
  const y1 = Math.floor((y + r) / OBSTACLE_CHUNK);
  for (let cx = x0; cx <= x1; cx++) for (let cy = y0; cy <= y1; cy++) fn(cx, cy);
}

/** Kałuża pod punktem (x, y) albo null. */
export function hazardAt(s: GameState, x: number, y: number): HazardDef | null {
  const zone = zoneOf(s);
  const def = zone?.hazards;
  if (!def || def.perChunk === 0) return null;
  let found = false;
  chunksAround(x, y, def.radius[1], (cx, cy) => {
    if (found) return;
    found = chunkHazards(s.seed, cx, cy, def, zone.obstacles).some((h) => Math.hypot(h.x - x, h.y - y) < h.radius);
  });
  return found ? def : null;
}

/** Mnożnik prędkości bohatera na jego obecnym miejscu (kałuża spowalnia). */
export function hazardSpeedFactor(s: GameState): number {
  const def = hazardAt(s, s.player.x, s.player.y);
  return def ? 1 - def.slow : 1;
}

/** Zasięg sprawdzania obiektów wokół bohatera (dalej nie sięga żaden atak). */
const BREAK_REACH = 640;

/** Kałuże ranią, a ciosy, wiry, pociski i sam bohater rozbijają obiekty. */
export function updateMapFeatures(s: GameState, dt: number): void {
  const zone = zoneOf(s);
  if (!zone) return;
  const p = s.player;

  const hazard = hazardAt(s, p.x, p.y);
  if (hazard && hazard.damagePerSecond > 0) {
    s.hazardTimer -= dt;
    if (s.hazardTimer <= 0) {
      s.hazardTimer = HAZARD_TICK;
      hurtPlayer(s, hazard.damagePerSecond * HAZARD_TICK);
    }
  } else s.hazardTimer = HAZARD_TICK / 2;

  if (zone.breakables === 0) return;
  const reach = s.content.game.playerRadius;
  chunksAround(p.x, p.y, BREAK_REACH, (cx, cy) => {
    for (const b of chunkBreakables(s.seed, cx, cy, zone.breakables, zone.obstacles)) {
      if (Math.abs(b.x - p.x) > BREAK_REACH || Math.abs(b.y - p.y) > BREAK_REACH) continue;
      if (s.broken.includes(b.key)) continue;
      if (Math.hypot(b.x - p.x, b.y - p.y) < reach + b.radius || hitByAttack(s, b)) smash(s, b);
    }
  });
}

/** Czy obiekt dostał w tym kroku: wir (nova), cios wręcz (łuk) albo pocisk bohatera. */
function hitByAttack(s: GameState, b: Breakable): boolean {
  for (const e of s.events) {
    if (e.type === 'nova') {
      if (Math.hypot(b.x - e.x, b.y - e.y) < e.radius + b.radius) return true;
    } else if (e.type === 'slash') {
      const dx = b.x - e.x;
      const dy = b.y - e.y;
      if (Math.hypot(dx, dy) > e.radius + b.radius) continue;
      let diff = Math.atan2(dy, dx) - e.angle;
      diff = Math.atan2(Math.sin(diff), Math.cos(diff));
      if (Math.abs(diff) <= e.arc / 2) return true;
    }
  }
  return s.projectiles.some((pr) => pr.life > 0 && Math.hypot(pr.x - b.x, pr.y - b.y) < pr.radius + b.radius);
}

function smash(s: GameState, b: Breakable): void {
  s.broken.push(b.key);
  s.events.push({ type: 'breakableBroken', x: b.x, y: b.y, variant: b.variant });
  const cfg = s.content.game.loot;
  const reward = zoneOf(s)?.rewardMultiplier ?? 1;
  if (nextRandom(s) < cfg.breakableGoldChance) {
    const [lo, hi] = cfg.breakableCoins;
    addDrop(s, 'gold', b.x, b.y, Math.max(1, Math.round((lo + nextRandom(s) * (hi - lo)) * reward)));
  }
  if (nextRandom(s) < cfg.breakablePotionChance) addDrop(s, 'potion', b.x, b.y, 1);
}
